import { Box, Stack, Typography } from '@mui/material';
import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { breakpoints, palette } from '../../../../assets/styles/themes/tokens';
import { getCurrentConversation } from '../../../../reducers/aiAssistant.selectors';
import {
	AIAssistantDisplayMode,
	aiAssistantContext,
} from '../../../../state/AIAssistantContextProvider';
import { aiAssistantEditorContext } from '../../../../state/AIAssistantEditorProvider';
import useScreenSize from '../../../../utils/useScreenSize';
import PodcastLoadingView from '../../Page/PodcastLoadingView';
import { DIALOG_WIDTH, TEXT_EDITOR_WIDTH } from '../constants';
import AIAssistantFooter from '../Footer/AIAssistantFooter';
import AIAssistantDialogContentWithHeadline from '../Layout/AIAssistantContentWithHeadline';
import AIContextHeadline from '../Layout/AIContextHeadline';
import { AIAssistantTextEditorContainer } from '../TextEditor';
import AIAssistantChatCollapseButton from './AIAssistantChatCollapseButton';
import AIAssistantMessages from './Messages/AIAssistantMessages';
import useAIAssistantAutoScroll from './useAIAssistantAutoScroll';
import useIsPodcastLoadingViewActive from './useIsPodcastLoadingViewActive';

const getMainContainerStyles = (
	shouldApplyPageModeStyles: boolean,
	isTextEditorOpen: boolean,
	isXsOrSmScreen: boolean
) => {
	return shouldApplyPageModeStyles
		? {
				display: 'flex',
				flexDirection: 'row',
				width: '100%',
				alignItems: 'stretch',
				flex: '1 0 0',
				alignSelf: 'stretch',
				height: '100%',
				margin: '0 auto',
				overflow: 'hidden',
			}
		: ({
				display: 'grid',
				...(isTextEditorOpen && !isXsOrSmScreen
					? { gridTemplateColumns: `${TEXT_EDITOR_WIDTH}px 1fr` }
					: { gridTemplateColumns: '1fr' }),
				overflow: 'hidden',
				height: '100%',
			} as const);
};

const getTextEditorWrapperStyles = (shouldApplyPageModeStyles: boolean) => {
	return shouldApplyPageModeStyles
		? {
				display: 'flex',
				flex: '1 1 0',
				minWidth: 0,
				alignSelf: 'stretch',
				height: '100%',
			}
		: {};
};

const getChatContentStyles = (
	shouldApplyPageModeStyles: boolean,
	isTextEditorOpen: boolean,
	isXsOrSmScreen: boolean,
	isChatCollapsed: boolean
) => {
	return shouldApplyPageModeStyles
		? {
				display: isChatCollapsed ? 'none' : 'flex',
				width: `${DIALOG_WIDTH.SLIM}px`,
				minWidth: '320px',
				maxWidth: `${breakpoints.mobile}px`,
				flexShrink: 0,
				flexDirection: 'column',
				alignItems: 'flex-start',
				alignSelf: 'stretch',
				borderRadius: 0,
				borderLeft: `1px solid ${palette.divider}`,
				height: '100%',
				overflow: 'hidden',
			}
		: {
				...(isTextEditorOpen && !isXsOrSmScreen ? { gridColumn: '2 / 3' } : { gridColumn: '1 / 2' }),
				overflow: 'hidden',
				height: '100%',
			};
};

const AIAssistantChat = () => {
	const { t } = useTranslation();
	const { isXsScreen, isSmScreen } = useScreenSize();
	const { isTextEditorOpen } = useContext(aiAssistantEditorContext);
	const { displayMode } = useContext(aiAssistantContext);

	useAIAssistantAutoScroll();

	const contentStyles = {
		position: 'relative',
		padding: 0,
		width: '100%',
		backgroundColor: palette.background.paperElevation1,
	};
	const isXsOrSmScreen = isXsScreen || isSmScreen;
	const isPageMode = displayMode === AIAssistantDisplayMode.PAGE;
	const [isChatCollapsed, setIsChatCollapsed] = useState(false);
	// While the podcast generates and until its detail opens, the loading view stands in
	// for the left pane; the editor takes the pane back the moment it has content.
	const isPodcastLoadingViewActive = useIsPodcastLoadingViewActive();

	const toggleChatCollapsed = () => {
		setIsChatCollapsed(!isChatCollapsed);
	};
	// On small screens, always render text editor (for SwipeableDrawer to work).
	// On larger screens, render when the editor is open or the podcast is generating.
	const shouldRenderTextEditor = isXsOrSmScreen || isTextEditorOpen || isPodcastLoadingViewActive;
	const mindItemContext = useSelector(getCurrentConversation)?.data?.context?.mindItem;

	const shouldApplyPageModeStyles =
		isPageMode && (isTextEditorOpen || isPodcastLoadingViewActive) && !isXsOrSmScreen;
	const shouldShowDivider = shouldApplyPageModeStyles;

	const mainContainerStyles = getMainContainerStyles(
		shouldApplyPageModeStyles,
		isTextEditorOpen,
		isXsOrSmScreen
	);
	const textEditorWrapperStyles = getTextEditorWrapperStyles(shouldApplyPageModeStyles);
	const chatContentStyles = getChatContentStyles(
		shouldApplyPageModeStyles,
		isTextEditorOpen,
		isXsOrSmScreen,
		isChatCollapsed
	);

	return (
		<Stack sx={mainContainerStyles}>
			{shouldRenderTextEditor &&
				(shouldApplyPageModeStyles ? (
					<Box sx={{ ...textEditorWrapperStyles, position: 'relative' }}>
						{isPodcastLoadingViewActive && !isTextEditorOpen ? (
							<PodcastLoadingView />
						) : (
							<AIAssistantTextEditorContainer />
						)}
						{shouldShowDivider && (
							<AIAssistantChatCollapseButton
								isCollapsed={isChatCollapsed}
								onToggle={toggleChatCollapsed}
							/>
						)}
					</Box>
				) : (
					<AIAssistantTextEditorContainer />
				))}
			<Stack sx={chatContentStyles} data-testid="ai-assistant-chat-pane">
				<AIAssistantDialogContentWithHeadline
					headline={mindItemContext ? <AIContextHeadline /> : null}
					data-testid="ai-assistant-chat-content"
					sx={contentStyles}
				>
					<AIAssistantMessages />
				</AIAssistantDialogContentWithHeadline>
				<AIAssistantFooter
					caption={
						<Typography variant="caption" sx={{ color: palette.text.secondary }}>
							{t('Please check the AI results for technical accuracy and pedagogical suitability.')}
						</Typography>
					}
				/>
			</Stack>
		</Stack>
	);
};

export default AIAssistantChat;
